import {
  Autocomplete,
  Box,
  CircularProgress,
  Popper,
  Paper,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { DateCalendar, DateField } from '@mui/x-date-pickers';
import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { useController } from 'react-hook-form';
import { NumericFormat } from 'react-number-format';
import { format, isValid } from 'date-fns';
import Iconify from '../iconify';
import ListboxComponent from '../autocomplete/ListboxComponent';
import { bobAIIcon } from '../bobAI/bobAI';
import { InvoiceParsedField, InvoiceGLItemParsedField } from '../../utils/globalEnums';
import { toDateOnlyString, parseDateOnlyToLocalDate } from '../../utils/formatTime';
import { useFieldMappingContext } from './FieldMappingContext';
import CurrencyCalc from './CurrencyCalc';

FieldMapping.propTypes = {
  fieldName: PropTypes.string,
  name: PropTypes.string, //name of the value
  fieldType: PropTypes.string,
  onChange: PropTypes.func,
  renderOption: PropTypes.func,
  options: PropTypes.array,
  disabled: PropTypes.bool,
  control: PropTypes.any,
  tooltipText: PropTypes.string,
  sx: PropTypes.object,
  showError: PropTypes.bool,
  paperSx: PropTypes.object,
  autocompletePaperSx: PropTypes.object,
  loading: PropTypes.bool,
};

export default function FieldMapping({
  name,
  control,
  fieldType,
  onChange,
  options = [],
  disabled = false,
  tooltipText = '',
  fieldName = '',
  sx,
  showError = false,
  renderOption,
  loading = false,
  autocompletePaperSx = {},
  ...otherProps
}) {
  const { formType, parsedFields, onBobAIHoverStart, onBobAIHoverEnd } = useFieldMappingContext();

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [dateEditing, setDateEditing] = useState(false);
  const anchorRef = useRef(null);
  const {
    field,
    formState: { errors },
  } = useController({ name, control });

  const fieldStyle = {
    fontSize: '14px',
    display: 'inline-block',
    whiteSpace: 'nowrap',
  };
  const valueStyle = {
    fontSize: '12px',
    color: '#060606',
  };

  const parsedDate =
    fieldType === 'date'
      ? (() => {
          const parsed = parseDateOnlyToLocalDate(field.value);
          return isValid(parsed) ? parsed : null;
        })()
      : null;

  const CustomListboxComponent = React.forwardRef((props, ref) => (
    <ListboxComponent {...props} ref={ref} selectedValue={field.value} />
  ));

  const bobAIHoverProps = formType === 'invoice'
    ? {
        onMouseEnter: () => onBobAIHoverStart?.({ fieldName: name }),
        onMouseLeave: () => onBobAIHoverEnd?.(),
      }
    : {};

  return (
    <>
      <Box sx={{ ...sx }}>
        <Stack spacing={1} direction={'row'} alignItems={'center'}>
          <Typography sx={fieldStyle} variant="body2" color={errors?.[name] || showError ? 'error' : '#060606'}>
            {fieldName}:
          </Typography>

          {((formType === 'invoice' && (parsedFields & InvoiceParsedField[name]) > 0) ||
            (formType === 'glItems' && (parsedFields & InvoiceGLItemParsedField[name.split('.').pop()]) > 0)) &&
            bobAIIcon(bobAIHoverProps)
          }
          {fieldType === 'text' && (
            <Tooltip title={tooltipText}>
              <>
                {' '}
                <TextField
                  name={name}
                  variant="standard"
                  onChange={(e) => {
                    field.onChange();
                    onChange(e);
                  }}
                  sx={{ flexGrow: 1 }}
                  value={field.value || ''}
                  onFocus={undefined}
                  onBlur={undefined}
                  disabled={disabled || loading}
                  inputRef={field.ref}
                  size="small"
                  InputProps={{
                    endAdornment: <>{loading ? <CircularProgress color="inherit" size={16} /> : null}</>,
                    sx: { fontSize: '12px', mt: '5px' },
                  }}
                  fullWidth
                  autoComplete="off"
                />
              </>
            </Tooltip>
          )}
          {fieldType === 'currency' && (
            <NumericFormat
              customInput={TextField}
              name={name}
              variant="standard"
              onValueChange={(e) => {
                field.onChange();
                onChange(e?.floatValue ?? 0);
              }}
              value={field.value || ''}
              disabled={disabled || loading}
              inputRef={field.ref}
              thousandSeparator
              decimalScale={2}
              fixedDecimalScale
              sx={{ flexGrow: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <span style={{ fontSize: '12px' }}>$</span>
                  </InputAdornment>
                ),
                endAdornment: <>{loading ? <CircularProgress color="inherit" size={16} /> : null}</>,
                sx: { fontSize: '12px', flexGrow: 1 },
              }}
            />
          )}
          {fieldType === 'currencyCalc' && (
            <CurrencyCalc
              name={name}
              onChange={onChange}
              control={control}
              disabled={disabled || loading}
              loading={loading}
            />
          )}

          {fieldType === 'autocomplete' && (
            <Tooltip title={tooltipText}>
              <Autocomplete
                key={`${field.value}-${options.length}`}
                value={field.value ? options?.find((option) => option.value === field.value) : null}
                onChange={onChange}
                options={options}
                disableClearable
                ListboxComponent={options.length > 1000 ? CustomListboxComponent : undefined}
                sx={{ flexGrow: 1 }}
                disabled={disabled || loading}
                renderOption={(props, option) => {
                  if (renderOption) {
                    return renderOption(props, option);
                  }
                  return (
                    <li
                      {...props}
                      style={{
                        fontSize: '12px',
                        ...(option.value === 'add-vendor' && {
                          textDecoration: 'underline',
                          color: '#1976d2',
                          cursor: 'pointer',
                          position: 'sticky',
                          top: 0,
                          background: 'white',
                          zIndex: 1,
                        }),
                      }}
                    >
                      {option.label}
                    </li>
                  );
                }}
                slotProps={{
                  paper: {
                    sx: {
                      border: '1px solid #00000049',
                      '& .MuiAutocomplete-option': {
                        fontSize: '12px',
                      },
                      ...(autocompletePaperSx || {}),
                    },
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    inputRef={field.ref}
                    variant="standard"
                    autoComplete="off"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                      sx: {
                        fontSize: '12px',
                        '& .MuiInputBase-input': {
                          ...valueStyle,
                        },
                      },
                    }}
                  />
                )}
                {...otherProps}
              />
            </Tooltip>
          )}

          {fieldType === 'select' && (
            <FormControl variant="standard" sx={{ flexGrow: 1 }}>
              <Select
                style={valueStyle}
                value={field.value || ''}
                onChange={onChange}
                disabled={disabled || loading}
                inputRef={field.ref}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      border: '1px solid #00000049',
                    },
                  },
                }}
              >
                {options?.map((item, index) => (
                  <MenuItem key={index} style={{ fontSize: '12px' }} value={item?.value}>
                    {item?.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {fieldType === 'date' && (
            <>
              <IconButton
                ref={anchorRef}
                size="small"
                color="secondary"
                disabled={disabled || loading}
                onClick={() => {
                  setCalendarOpen((open) => !open);
                  setDateEditing(false);
                }}
              >
                <Iconify icon="mdi:calendar" />
              </IconButton>

              {dateEditing ? (
                <DateField
                  autoFocus
                  fullWidth
                  value={parsedDate}
                  format="MM/dd/yyyy"
                  onChange={(newDate) => {
                    if (newDate && isValid(newDate)) {
                      onChange(toDateOnlyString(newDate));
                    }
                  }}
                  onBlur={() => {
                    setDateEditing(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.currentTarget.blur();
                  }}
                  slotProps={{
                    textField: {
                      variant: 'standard',
                      InputProps: {
                        disableUnderline: true,
                      },
                      sx: {
                        minWidth: 120,
                        '& .MuiInputBase-input': {
                          fontSize: '0.875rem',
                          color: 'secondary.main',
                          px: 0,
                          py: 0.5,
                        },
                      },
                    },
                  }}
                />
              ) : (
                <Typography
                  variant="subtitle2"
                  color="secondary"
                  component="span"
                  sx={{
                    textDecoration: 'underline',
                    cursor: disabled ? 'default' : 'text',
                    userSelect: 'none',
                  }}
                  onClick={() => {
                    if (!disabled) {
                      setDateEditing(true);
                      setCalendarOpen(false);
                    }
                  }}
                >
                  {parsedDate ? format(parsedDate, 'MM/dd/yyyy') : 'Choose Date'}
                </Typography>
              )}
              <Popper
                open={calendarOpen}
                anchorEl={anchorRef.current}
                placement="bottom-start"
                disablePortal={false}
                modifiers={[
                  { name: 'offset', options: { offset: [0, 8] } },
                  { name: 'preventOverflow', options: { boundary: 'viewport' } },
                ]}
                sx={{ zIndex: (theme) => theme.zIndex.modal }}
              >
                <Paper elevation={3}>
                  <DateCalendar
                    value={parsedDate}
                    onChange={(newDate) => {
                      if (newDate && isValid(newDate)) {
                        onChange(toDateOnlyString(newDate));
                      }
                      setCalendarOpen(false);
                    }}
                    onClose={() => setCalendarOpen(false)}
                  />
                </Paper>
              </Popper>
            </>
          )}
        </Stack>
      </Box>
    </>
  );
}
